import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  FiAlertTriangle,
  FiArrowUp,
  FiBookOpen,
  FiExternalLink,
  FiRefreshCw,
} from "react-icons/fi";

import { isApiClientError, isApiTransportError } from "../../api/errors";
import { resolveApiUrl } from "../../config/environment";
import { hasPermission } from "../../entities/access/permissions";
import { shellBootstrapQuery } from "../bootstrap";
import type { ShellServices } from "../services";
import "./guide.css";
import { GuideBlocks } from "./GuideBlocks";
import { GuideContext, type GuideContextValue } from "./guideContext";
import { GuideLightbox } from "./GuideLightbox";
import { GuideToc } from "./GuideToc";
import {
  GUIDE_FALLBACK_URL,
  collectImages,
  classifyLink,
  formatAgo,
  formatMoment,
  userGuideKey,
} from "./guideSupport";
import {
  prefersReducedMotion,
  useActiveHeading,
  useNow,
  useScrolledAway,
} from "./useGuideScroll";

/** Как часто спрашивать, собралась ли копия, пока её ещё нет. */
const PENDING_POLL_MS = 4000;
/** Как часто спрашивать результат после нажатия «Проверить обновления». */
const CHECK_POLL_MS = 2500;
/** Дольше этого ждать ответа проверки незачем: копия обновится сама. */
const CHECK_TIMEOUT_MS = 20_000;
/** Подписанная ссылка на картинку могла истечь: обновляем данные, но не чаще раза в полминуты. */
const IMAGE_REFRESH_GAP_MS = 30_000;

interface PendingCheck {
  readonly baseCheckedAt: string | null;
  readonly baseVersion: number | null;
}

export function GuidePage({ services }: { services: ShellServices }) {
  const api = services.userGuide;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const hash = useLocation({ select: (location) => location.hash });
  const [check, setCheck] = useState<PendingCheck | null>(null);
  const [notice, setNotice] = useState<{
    tone: "info" | "error";
    text: string;
  } | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const top = useRef<HTMLDivElement>(null);
  const lastImageRefresh = useRef(0);
  const scrolledToHash = useRef(false);
  const now = useNow(60_000);
  // Уже загружен оболочкой при входе в раздел (право проверено в маршруте) —
  // здесь только читаем из кэша, второго запроса это не создаёт.
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const canManage = hasPermission(
    { permissions: bootstrap.data?.permissions ?? [] },
    "user-guide.manage",
  );

  const query = useQuery({
    queryKey: userGuideKey,
    queryFn: async () => {
      if (api === undefined)
        throw new Error("User guide API is not available.");
      return (await api.get()).data;
    },
    // Копия меняется редко, а подписанные ссылки на картинки живут около часа:
    // полчаса свежести и повторная проверка при возвращении на вкладку.
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    refetchInterval: (state) => {
      if (check !== null) return CHECK_POLL_MS;
      const data = state.state.data;
      return data?.status === "pending" && !data.sync_failing
        ? PENDING_POLL_MS
        : false;
    },
  });
  const guide = query.data;

  const refresh = useMutation({
    mutationFn: async () => {
      if (api === undefined)
        throw new Error("User guide API is not available.");
      return (await api.refresh()).data;
    },
    onMutate: () => setNotice(null),
    onSuccess: (result) => {
      if (result.status === "unavailable") {
        setNotice({
          tone: "error",
          text: "Связь с Confluence не настроена. Обратитесь к администратору ERP.",
        });
        return;
      }
      setCheck({
        baseCheckedAt: guide?.checked_at ?? null,
        baseVersion: guide?.source_version ?? null,
      });
    },
    onError: (error) => {
      setNotice({
        tone: "error",
        text:
          isApiClientError(error) && error.status === 429
            ? "Проверять можно не чаще нескольких раз в минуту. Подождите немного."
            : "Не удалось запустить проверку. Попробуйте ещё раз чуть позже.",
      });
    },
  });

  // Проверка закончилась, когда у копии появилось новое время проверки. С
  // часами браузера это не сравнивается: они могут расходиться с сервером.
  useEffect(() => {
    if (check === null || guide === undefined) return;
    if (guide.checked_at === check.baseCheckedAt) return;
    setCheck(null);
    if (guide.sync_failing) {
      setNotice({
        tone: "error",
        text: "Confluence сейчас не отвечает. Показана последняя загруженная версия, проверка повторится сама.",
      });
    } else if (guide.source_version !== check.baseVersion) {
      setNotice({
        tone: "info",
        text:
          guide.source_version === null
            ? "Инструкция обновлена."
            : `Инструкция обновлена до версии ${guide.source_version}.`,
      });
    } else {
      setNotice({
        tone: "info",
        text: "Изменений нет: у вас самая свежая версия.",
      });
    }
  }, [check, guide]);

  useEffect(() => {
    if (check === null) return;
    const timer = window.setTimeout(() => {
      setCheck(null);
      setNotice({
        tone: "info",
        text: "Проверка идёт дольше обычного. Инструкция обновится сама, как только Confluence ответит.",
      });
    }, CHECK_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [check]);

  const blocks = useMemo(() => guide?.blocks ?? [], [guide]);
  const headings = useMemo(() => guide?.headings ?? [], [guide]);
  const images = useMemo(() => collectImages(blocks), [blocks]);
  const headingIds = useMemo(
    () => headings.filter((item) => item.level <= 3).map((item) => item.id),
    [headings],
  );
  const ready = guide?.status === "ready";
  const activeId = useActiveHeading(headingIds, ready);
  const scrolledAway = useScrolledAway(top);

  const goToSection = useCallback(
    (id: string) => {
      const element = document.getElementById(id);
      if (element === null) return;
      element.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "start",
      });
      // Клавиатура и экранный диктор продолжают чтение с выбранного раздела.
      element.setAttribute("tabindex", "-1");
      element.focus({ preventScroll: true });
      // Адрес раздела можно скопировать и отправить коллеге.
      void navigate({ hash: id, replace: true, hashScrollIntoView: false });
    },
    [navigate],
  );

  // Ссылка на раздел, открытая с нуля: статья грузится позже, чем роутер
  // пытается прокрутить, поэтому к разделу переходим сами, когда он появился.
  useEffect(() => {
    if (!ready || hash === "" || scrolledToHash.current) return;
    scrolledToHash.current = true;
    document
      .getElementById(hash)
      ?.scrollIntoView({ behavior: "auto", block: "start" });
  }, [ready, hash]);

  const imageFailed = useCallback(() => {
    const at = Date.now();
    if (at - lastImageRefresh.current < IMAGE_REFRESH_GAP_MS) return;
    lastImageRefresh.current = at;
    void queryClient.invalidateQueries({ queryKey: userGuideKey });
  }, [queryClient]);

  const context = useMemo<GuideContextValue>(
    () => ({
      images: guide?.images ?? {},
      openImage: (imageId) => {
        const index = images.findIndex((image) => image.imageId === imageId);
        if (index >= 0) setLightbox(index);
      },
      imageFailed,
      goToSection,
    }),
    [guide?.images, images, imageFailed, goToSection],
  );

  if (query.isPending) return <GuideSkeleton />;
  if (query.isError || guide === undefined) {
    return (
      <GuideShell title="Инструкция">
        <GuideProblem
          title="Не удалось загрузить инструкцию"
          text={
            isApiTransportError(query.error)
              ? "Нет связи с сервером. Проверьте подключение к интернету и повторите попытку."
              : "Сервер не ответил. Повторите попытку через несколько секунд."
          }
          action={{ label: "Повторить", onClick: () => void query.refetch() }}
        />
      </GuideShell>
    );
  }

  if (guide.status === "unavailable") {
    return (
      <GuideShell title="Инструкция">
        <GuideProblem
          title="Инструкция ещё не подключена"
          tone="neutral"
          text={
            canManage
              ? "Приложение Confluence заведено, но согласие ещё не дано. Нажмите кнопку ниже — откроется экран Atlassian, дальше подключение обновляется само."
              : "Администратор ERP пока не настроил связь с Confluence, где ведётся инструкция. Пока её можно открыть напрямую."
          }
          navigate={
            canManage
              ? {
                  label: "Подключить Confluence",
                  href: resolveApiUrl("/api/v1/user-guide/oauth/start"),
                }
              : undefined
          }
          link={{ label: "Открыть в Confluence", href: GUIDE_FALLBACK_URL }}
        />
      </GuideShell>
    );
  }

  if (guide.status === "pending") {
    return guide.sync_failing ? (
      <GuideShell title="Инструкция">
        <GuideProblem
          title="Не удалось загрузить инструкцию"
          text="ERP не смогла получить страницу из Confluence: сервис недоступен или доступ настроен неверно. Сообщите администратору, а пока откройте инструкцию напрямую."
          code={guide.error_code}
          link={{
            label: "Открыть в Confluence",
            href: guide.source_url ?? GUIDE_FALLBACK_URL,
          }}
        />
      </GuideShell>
    ) : (
      <GuideSkeleton
        label="Загружаем инструкцию из Confluence"
        hint="Первая загрузка занимает до минуты: страницу и скриншоты ERP забирает один раз и дальше хранит у себя."
      />
    );
  }

  const title = guide.title ?? "Инструкция";
  const updated = formatMoment(guide.source_updated_at);
  const checked = formatAgo(guide.checked_at, now);
  const checking = check !== null || refresh.isPending;

  return (
    <GuideContext.Provider value={context}>
      <main className="guide-page" aria-labelledby="guide-title">
        <div ref={top} className="guide-sentinel" aria-hidden="true" />
        <header className="guide-hero">
          <h1 id="guide-title">{title}</h1>
          <div className="guide-hero__bar">
            <ul className="guide-meta">
              <li>
                <span
                  className={`status-badge status-badge--${guide.sync_failing ? "pending" : "ready"}`}
                >
                  {guide.sync_failing
                    ? "Обновления не проверены"
                    : "Актуальная версия"}
                </span>
              </li>
              {updated === null ? null : (
                <li>
                  <span className="guide-meta__label">Обновлена</span>{" "}
                  <time dateTime={guide.source_updated_at ?? undefined}>
                    {updated}
                  </time>
                </li>
              )}
              {checked === null ? null : (
                <li>
                  <span className="guide-meta__label">Проверено</span> {checked}
                </li>
              )}
            </ul>
            <div className="guide-actions">
              <button
                className="button button--secondary button--compact"
                type="button"
                disabled={checking}
                onClick={() => refresh.mutate()}
              >
                <FiRefreshCw
                  aria-hidden="true"
                  className={checking ? "guide-spin" : undefined}
                />
                {checking ? "Проверяем…" : "Проверить обновления"}
              </button>
              {classifyLink(guide.source_url) === null ? null : (
                <a
                  className="button button--text button--compact"
                  href={guide.source_url ?? undefined}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <FiExternalLink aria-hidden="true" />
                  Открыть в Confluence
                  <span className="visually-hidden"> (в новой вкладке)</span>
                </a>
              )}
            </div>
          </div>
          <div
            className={`guide-notice${notice === null ? "" : ` guide-notice--${notice.tone}`}`}
            role="status"
            aria-live="polite"
          >
            {notice === null && guide.sync_failing
              ? `Показана последняя загруженная версия. Проверка повторится автоматически.${
                  guide.error_code === null
                    ? ""
                    : ` Код ошибки: ${guide.error_code}.`
                }`
              : notice?.text}
          </div>
        </header>

        <div className="guide-layout">
          <GuideToc
            headings={headings}
            activeId={activeId}
            onSelect={goToSection}
            variant="disclosure"
          />
          <GuideToc
            headings={headings}
            activeId={activeId}
            onSelect={goToSection}
            variant="rail"
          />
          <article className="guide-article">
            <GuideBlocks blocks={blocks} lead />
          </article>
        </div>

        {scrolledAway ? (
          <button
            className="guide-top"
            type="button"
            onClick={() =>
              window.scrollTo({
                top: 0,
                behavior: prefersReducedMotion() ? "auto" : "smooth",
              })
            }
            aria-label="Наверх"
          >
            <FiArrowUp aria-hidden="true" />
          </button>
        ) : null}

        {lightbox === null ? null : (
          <GuideLightbox
            images={images}
            links={guide.images}
            index={lightbox}
            onIndexChange={setLightbox}
            onClose={() => setLightbox(null)}
          />
        )}
      </main>
    </GuideContext.Provider>
  );
}

function GuideShell({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <main className="guide-page" aria-labelledby="guide-title">
      <header className="guide-hero">
        <div className="guide-hero__text">
          <h1 id="guide-title">{title}</h1>
        </div>
      </header>
      {children}
    </main>
  );
}

function GuideProblem({
  title,
  text,
  code,
  tone = "error",
  action,
  navigate,
  link,
}: {
  title: string;
  text: string;
  /** Классификация причины сбоя (confluence_not_found и т.п.) — для того, кто настраивал интеграцию. */
  code?: string | null;
  /** Ошибка красная; «ещё не подключено» — штатное состояние, оно нейтральное. */
  tone?: "error" | "neutral";
  action?: { label: string; onClick: () => void };
  /** Переход в том же окне: экран согласия Atlassian вернёт обратно на /guide. */
  navigate?: { label: string; href: string };
  link?: { label: string; href: string };
}) {
  return (
    <section
      className={`guide-problem guide-problem--${tone}`}
      role={tone === "error" ? "alert" : "status"}
    >
      <span className="guide-problem__icon" aria-hidden="true">
        {tone === "error" ? <FiAlertTriangle /> : <FiBookOpen />}
      </span>
      <div>
        <h2>{title}</h2>
        <p>{text}</p>
        {code === null || code === undefined ? null : (
          <p className="guide-problem__code">
            Код ошибки: <code>{code}</code>
          </p>
        )}
        <div className="guide-problem__actions">
          {action === undefined ? null : (
            <button
              className="button button--secondary button--compact"
              type="button"
              onClick={action.onClick}
            >
              {action.label}
            </button>
          )}
          {navigate === undefined ||
          classifyLink(navigate.href) === null ? null : (
            <a
              className="button button--primary button--compact"
              href={navigate.href}
            >
              {navigate.label}
            </a>
          )}
          {link === undefined || classifyLink(link.href) === null ? null : (
            <a
              className="button button--secondary button--compact"
              href={link.href}
              target="_blank"
              rel="noreferrer noopener"
            >
              <FiExternalLink aria-hidden="true" />
              {link.label}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

/** Каркас страницы, пока копия грузится: шапка, оглавление и первые абзацы. */
function GuideSkeleton({
  label = "Загружаем инструкцию",
  hint,
}: {
  label?: string;
  hint?: string;
}) {
  return (
    <main className="guide-page" aria-busy="true" aria-label={label}>
      <header className="guide-hero">
        <div className="guide-hero__text">
          <div className="guide-skeleton guide-skeleton--title" />
          <div className="guide-skeleton guide-skeleton--meta" />
        </div>
      </header>
      <p className="guide-notice guide-notice--info" role="status">
        {label}…{hint === undefined ? null : <span> {hint}</span>}
      </p>
      <div className="guide-layout" aria-hidden="true">
        <div className="guide-article">
          <div className="guide-skeleton guide-skeleton--lead" />
          <div className="guide-skeleton guide-skeleton--line" />
          <div className="guide-skeleton guide-skeleton--line" />
          <div className="guide-skeleton guide-skeleton--short" />
          <div className="guide-skeleton guide-skeleton--figure" />
          <div className="guide-skeleton guide-skeleton--line" />
          <div className="guide-skeleton guide-skeleton--short" />
        </div>
      </div>
    </main>
  );
}
